import React, { useState, useCallback, useEffect } from 'react';
import texasApi from '../../services/texasApi';
import { landlordApi } from '../../services/api';

export const TexasScore: React.FC = () => {
  const [scores, setScores] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await landlordApi.get('api/texas/security-score/portfolio');
      const d = res?.data?.data || res?.data;
      if (Array.isArray(d)) {
        setScores(d);
      } else if (d?.scores) {
        setScores(d.scores);
      } else {
        setScores([]);
      }
    } catch {
      try {
        const res = await landlordApi.get('api/texas/compliance-dashboard/portfolio');
        const d = res?.data?.data || res?.data;
        setScores(Array.isArray(d) ? d : []);
      } catch {
        setError('Failed to load scores');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const avgScore = scores.length > 0
    ? Math.round(scores.reduce((sum: number, s: any) => sum + (s.score ?? s.securityScore ?? 0), 0) / scores.length)
    : 0;

  const handleRefresh = async () => {
    await loadData();
  };

  return (
    <div data-testid="texas-score" className="texas-page-container">
      <h1>Texas Score</h1>
      <p className="page-description">
        Security & compliance scoring across Texas properties.
      </p>

      {error && <div className="error" data-testid="error">{error}</div>}

      {!loading && (
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-bold text-slate-500 tracking-wider">AVERAGE SCORE</div>
              <div className="text-5xl font-black text-emerald-600 mt-1">{avgScore}</div>
            </div>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-slate-800 text-white rounded text-sm hover:bg-slate-700"
              data-testid="refresh-scores-btn"
            >Refresh</button>
          </div>
        </section>
      )}

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h2>Property Scores</h2>
        {loading ? (
          <div className="no-results">Loading scores...</div>
        ) : scores.length === 0 ? (
          <div className="no-results">No property scores found</div>
        ) : (
          <div className="space-y-2">
            {scores.map((item: any, idx: number) => {
              const name = item.propertyName || item.name || `Property ${idx + 1}`;
              const itemScore = item.score ?? item.securityScore ?? 0;
              return (
                <div key={item.id || idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200/80 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-gray-900 text-xs">{name}</div>
                    <div className="text-xs text-slate-500">{item.propertyId || ''}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      itemScore >= 80 ? 'bg-emerald-100 text-emerald-700' :
                      itemScore >= 50 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {itemScore}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default TexasScore;
